// Password validation aligned with NIST SP 800-63B and CIS benchmarks.
//
// Key principles (NIST 800-63B):
// - Minimum 14 characters (CIS benchmark; NIST minimum is 8)
// - NO composition rules (no mandatory uppercase, numbers, symbols)
// - Check against known compromised / common passwords
// - Block repetitive and sequential patterns
// - Maximum 128 characters

// Top 200 most common passwords (sourced from public breach compilations).
// NIST 800-63B Section 5.1.1.2 requires checking against commonly-used passwords.
const COMMON_PASSWORDS = new Set([
  'password', '123456', '12345678', 'qwerty', 'abc123', 'monkey', 'master',
  'dragon', '111111', 'baseball', 'iloveyou', 'trustno1', 'sunshine',
  'princess', 'football', 'charlie', 'shadow', 'michael', 'qwerty123',
  'password1', 'password123', 'password1234', '1234567890', '123456789',
  '12345', '1234567', 'letmein', 'welcome', 'admin', 'login', 'starwars',
  'solo', 'passw0rd', 'master', 'hello', 'freedom', 'whatever', 'qazwsx',
  'batman', 'donald', 'photon', 'ashley', 'bailey', 'mustang', 'access',
  'flower', 'hottie', 'loveme', 'pepper', 'summer', 'winter', 'spring',
  'autumn', 'robert', 'matthew', 'jordan', 'daniel', 'andrew', 'joshua',
  'harley', 'ranger', 'thomas', 'soccer', 'hockey', 'killer', 'george',
  'hunter', 'chicken', 'thunder', 'ginger', 'hammer', 'silver', 'cookie',
  'jessica', 'jennifer', 'amanda', 'samantha', 'heather', 'melissa',
  'merlin', 'diamond', 'bigdog', 'cowboy', 'falcon', 'knight', 'yankee',
  'jordan23', 'maggie', 'golfing', 'peanut', 'buster', 'tigers', 'london',
  'liverpool', 'arsenal', 'chelsea', 'maverick', 'internet', 'computer',
  'samsung', 'dolphin', 'corvette', 'ferrari', 'porsche', 'mercedes',
  'phoenix', 'compaq', 'elephant', 'scorpion', 'skittles', 'jackson',
  'alexander', 'elizabeth', 'victoria', 'nicholas', 'benjamin', 'champion',
  'midnight', 'creative', 'butthole', 'nothing', 'zaq1zaq1', 'trustno1',
  'qwer1234', 'abcd1234', 'pass1234', 'test1234', 'changeme', 'default',
  'administrator', 'letmein1', 'welcome1', 'monkey123', 'dragon123',
  'abc12345', 'iloveyou1', 'p@ssw0rd', 'p@ssword', 'pa$$word', 'passw0rd1',
  'superman', 'spiderman', 'batman123', 'matrix', 'blahblah', 'asdfgh',
  'zxcvbn', 'asdfghjkl', 'zxcvbnm', 'qwertyuiop', 'poiuytrewq',
  '1q2w3e4r', '1qaz2wsx', 'q1w2e3r4', 'qwerty1', 'abcdef', 'abcdefg',
  'abcdefgh', '0987654321', '123321', '654321', '1234321', '11111111',
  '00000000', '88888888', '12341234', '1111111111', '0000000000',
  '1234561234', 'aaaaaaa', 'aaaaaaaaa', 'pass', 'test', 'guest', 'root',
])

/**
 * Validate a password against NIST SP 800-63B / CIS requirements.
 * Returns an object with individual check results and an overall valid flag.
 *
 * @param {string} password - The password to validate.
 * @param {string} [email] - Optional email to check for personal info.
 * @returns {{ checks: Array<{id: string, label: string, passed: boolean}>, valid: boolean }}
 */
export function validatePassword(password, email) {
  const checks = [
    {
      id: 'length',
      label: 'At least 14 characters',
      passed: password.length >= 14,
    },
    {
      id: 'maxLength',
      label: 'No more than 128 characters',
      passed: password.length <= 128,
    },
    {
      id: 'common',
      label: 'Not a commonly used password',
      passed: password.length > 0 && !COMMON_PASSWORDS.has(password.toLowerCase()),
    },
    {
      id: 'repetitive',
      label: 'No repetitive characters (e.g., aaa)',
      passed: password.length > 0 && !/(.)\1{2,}/.test(password),
    },
    {
      id: 'sequential',
      label: 'No sequential patterns (e.g., 1234, abcd)',
      passed: password.length > 0 && !hasSequentialPattern(password),
    },
  ]

  // Optional: check if password contains parts of the user's email
  if (email) {
    const localPart = email.split('@')[0]?.toLowerCase()
    if (localPart && localPart.length >= 4) {
      checks.push({
        id: 'personal',
        label: 'Does not contain your email username',
        passed: password.length > 0 && !password.toLowerCase().includes(localPart),
      })
    }
  }

  return {
    checks,
    valid: checks.every(c => c.passed),
  }
}

/**
 * Check for 4+ sequential ascending or descending characters.
 */
function hasSequentialPattern(password) {
  const lower = password.toLowerCase()
  for (let i = 0; i <= lower.length - 4; i++) {
    let ascending = true
    let descending = true
    for (let j = 0; j < 3; j++) {
      const diff = lower.charCodeAt(i + j + 1) - lower.charCodeAt(i + j)
      if (diff !== 1) ascending = false
      if (diff !== -1) descending = false
    }
    if (ascending || descending) return true
  }
  return false
}
